/* © 2024-2026 Johannes Roob. Proprietärer Code. Keine Nutzung oder Weitergabe ohne Erlaubnis. | Proprietary code. No use or distribution without permission. */

import { must, mustDataset, mustQuerySelector } from "../../util/must.js";
import { Form } from "./Form.js";
import { FormManager } from "../../util/FormManager.js";

const windowStateOpening = "Opening";
const windowStateMaximized = "Maximized";
const windowStateNormal = "Normal";

export class Window extends Form {
    #eventController = new AbortController();
    modalChildWindow: Window | undefined;
    modalParentWindow: Window | undefined;

    // constructor():
    #element: HTMLElement;
    #dataset: WindowDataset;
    #formHost: HTMLElement;
    #buttonMinimize: HTMLElement;
    #buttonMaximize: HTMLElement;
    #titleElement: HTMLElement;

    // For moving, resizing:
    #rect: DOMRect = new DOMRect();
    #offsetX = 0;
    #offsetY = 0;
    #animationFrameId: number | null = null;
    #resizeType = "";

    constructor(formManager: FormManager, element: HTMLElement, windowElement: HTMLElement) {
        super(formManager, element);

        if (!windowElement.matches("div.Window")) {
            throw new Error("this is not a Window");
        }

        this.#element = windowElement;
        this.#dataset = new WindowDataset(windowElement);

        // Make sure FormHost element exists.
        let formHost = windowElement.querySelector<HTMLElement>(".FormHost");

        if (formHost === null) {
            formHost = document.createElement("div");
            formHost.className = "FormHost";
            windowElement.appendChild(formHost);
        }

        this.#formHost = formHost;
        this.#buttonMinimize = mustQuerySelector<HTMLButtonElement>(windowElement, ".Window-ButtonMinimize");
        this.#buttonMaximize = mustQuerySelector<HTMLButtonElement>(windowElement, ".Window-ButtonMaximize");
        this.#titleElement = mustQuerySelector(windowElement, "div.Window-Title");
        this.#dataset.isModal = this.isModal;
    }

    init() {
        if (this.modalParentWindow === undefined) {
            // Position inside document window.
            let height = window.innerHeight - 128;
            const taskbar = this.formManager.windowManager.taskbar;

            if (taskbar !== undefined) {
                height = height - taskbar.getRect().height;
            }

            const width = window.innerWidth - 128;
            this.position(64, 64, height, width);
        } else {
            // Position "inside" the parent window.
            const rect = this.modalParentWindow.getRect();
            this.position(rect.top + 64, rect.left + 64,
                rect.height - 128, rect.width - 128);
        }

        this.addEvents();
        super.isVisible = true;
        this.playAnimationOpening();
    }

    destroy() {
        // Remove all events.
        this.#eventController.abort();
    }

    get element() {
        return this.#element;
    }

    get dataset() {
        return this.#dataset;
    }

    get formHost() {
        return this.#formHost;
    }

    get title(): string {
        if (this.#titleElement === null) {
            throw new Error("Window: Title element is missing");
        }

        return this.#titleElement.textContent;
    }

    set title(value: string) {
        if (this.#titleElement === null) {
            throw new Error("Window: Title element is missing");
        }

        this.#titleElement.textContent = value;
    }

    getRect() {
        return this.#element.getBoundingClientRect();
    }

    getOriginWindow() {
        return getFirstWindow(this);

    }

    getTerminalWindow() {
        return getLastWindow(this);
    }

    setIndex(index: number) {
        this.#element.style.zIndex = index.toString();
    }

    activate() {
        this.dataset.isActive = true;
    }

    unactivate() {
        this.dataset.isActive = false;
    }

    maximize() {
        // Change maximize button to restore button.
        this.#buttonMaximize.classList.add("Restore");
        // Determine Window height.
        let height = window.innerHeight;
        const taskbar = this.formManager.windowManager.taskbar;

        if (taskbar !== undefined) {
            height = height - taskbar.getRect().height;
        }

        // Remember height, width and inset and remove them from style.
        const e = this.#element;
        e.dataset.previousHeight = e.style.height;
        e.dataset.previousWidth = e.style.width;
        e.dataset.previousTranslate = e.style.translate;
        e.style.height = `${height}px`;
        e.style.width = "100vw";
        e.style.translate = "0 0";
        // Update window state.
        this.dataset.state = windowStateMaximized;
    }

    unmaximize() {
        // Change restore button to maximize button.
        this.#buttonMaximize.classList.remove("Restore");
        // Set previous style.
        const e = this.#element;
        e.style.height = mustDataset(e, "previousHeight");
        e.style.width = mustDataset(e, "previousWidth");
        e.style.translate = mustDataset(e, "previousTranslate");
        // Update window state.
        this.dataset.state = windowStateNormal;
    }

    playAnimationMinimize() {
        this.dataset.isMinimizing = true;

        this.#element.addEventListener("animationend", () => {
            this.dataset.isMinimizing = false;
            this.dataset.isMinimized = true;
            this.formManager.windowManager.activatePeek();
        }, { once: true });
    }

    playAnimationUnminimize() {
        this.dataset.isUnminimizing = true;

        this.#element.addEventListener("animationend", () => {
            this.dataset.isUnminimizing = false;
            this.dataset.isMinimized = false;
        }, { once: true });
    }

    playAnimationOpening() {
        this.dataset.state = windowStateOpening;

        this.#element.addEventListener("animationend", () => {
            this.dataset.state = undefined;
            super.focusFirstInput();
        }, { once: true });
    }

    playAnimationFlash() {
        this.dataset.isFlashing = true;

        this.#element.addEventListener("animationend", () => {
            this.dataset.isFlashing = false;
        }, { once: true });
    }

    position(top: number, left: number, height: number, width: number) {
        const e = this.#element;
        e.style.translate = `${left}px ${top}px`;
        e.style.height = height + "px";
        e.style.width = width + "px";
    }

    addEvents() {
        // Add event: Bring window to top on click or if descending element is clicked.
        const activateWindow = () => {
            this.formManager.windowManager.activate(this);
        };

        this.#element.addEventListener("pointerdown", activateWindow,
            {
                signal: this.#eventController.signal
            });

        // Add event: Maximize/restore window
        const maximizeWindow = () => {
            switch (this.dataset.state) {
                case windowStateMaximized:
                    this.unmaximize();
                    break;

                case windowStateNormal:
                    this.maximize();
                    break;
            }
        };

        this.#buttonMaximize.addEventListener("click", maximizeWindow,
            {
                signal: this.#eventController.signal
            });

        if (this.#titleElement !== null) {
            this.#titleElement.addEventListener("dblclick", maximizeWindow,
                {
                    signal: this.#eventController.signal
                }
            );
        }

        // Add event: Minimize window
        const minimizeWindow = () => {
            this.formManager.windowManager.minimize(this);
        };

        this.#buttonMinimize.addEventListener("click", minimizeWindow,
            {
                signal: this.#eventController.signal
            });

        // Add events: Move window
        this.addEventsMove();
        // Add events: Resize window
        this.addEventsResize();
    }

    addEventsMove() {
        if (this.#titleElement === null) {
            return;
        }

        const handle = this.#titleElement;

        const movePointerMove = (e: PointerEvent) => {
            e.preventDefault();

            if (this.#animationFrameId !== null) {
                return;
            }

            this.#animationFrameId = requestAnimationFrame(() => {
                if (this.dataset.state === windowStateMaximized) {
                    this.unmaximize();
                }

                const left = e.clientX + this.#offsetX;
                const top = e.clientY + this.#offsetY;
                this.#element.style.translate = `${left}px ${top}px`;
                this.#animationFrameId = null;
            });
        };

        const movePointerUp = (e: PointerEvent) => {
            this.dataset.isMoving = false;
            handle.releasePointerCapture(e.pointerId);
            handle.removeEventListener("pointermove", movePointerMove);
            handle.removeEventListener("pointerup", movePointerUp);
            handle.removeEventListener("pointercancel", movePointerUp);
            handle.removeEventListener("lostpointercapture", movePointerUp);

            if (this.#animationFrameId !== null) {
                cancelAnimationFrame(this.#animationFrameId);
                this.#animationFrameId = null;
            }
        };

        const movePointerDown = (e: PointerEvent) => {
            if (e.button !== 0 && e.pointerType !== "touch") return;
            e.preventDefault();
            const rect = this.getRect();
            this.#offsetX = rect.left - e.clientX;
            this.#offsetY = rect.top - e.clientY;
            this.dataset.isMoving = true;
            handle.setPointerCapture(e.pointerId);

            handle.addEventListener("pointermove", movePointerMove,
                {
                    passive: false,
                    signal: this.#eventController.signal
                });

            handle.addEventListener("pointerup", movePointerUp,
                {
                    passive: true, once: true,
                    signal: this.#eventController.signal
                });

            handle.addEventListener("pointercancel", movePointerUp,
                {
                    passive: true, once: true,
                    signal: this.#eventController.signal
                });

            handle.addEventListener("lostpointercapture", movePointerUp,
                {
                    passive: true, once: true,
                    signal: this.#eventController.signal
                });
        };

        handle.addEventListener("pointerdown", movePointerDown,
            {
                passive: false,
                signal: this.#eventController.signal
            });
    }

    addEventsResize() {
        const resizePointerMove = (e: PointerEvent) => {
            if (this.#animationFrameId !== null) {
                return;
            }

            this.#animationFrameId = requestAnimationFrame(() => {
                const rect = this.#rect;
                let left = rect.left;
                let top = rect.top;
                let height = rect.height;
                let width = rect.width;

                switch (this.#resizeType) {
                    case "Top":
                        top = e.clientY + this.#offsetY;
                        height = rect.bottom - e.clientY - this.#offsetY;
                        break;

                    case "TopLeft":
                        top = e.clientY + this.#offsetY;
                        left = e.clientX + this.#offsetX;
                        height = rect.bottom - e.clientY - this.#offsetY;
                        width = rect.right - e.clientX - this.#offsetX;
                        break;

                    case "TopRight":
                        top = e.clientY + this.#offsetY;
                        height = rect.bottom - e.clientY;
                        width = e.clientX - left + this.#offsetX;
                        break;

                    case "Right":
                        width = e.clientX - left + this.#offsetX;
                        break;

                    case "Bottom":
                        height = e.clientY - top + this.#offsetY;
                        break;

                    case "BottomLeft":
                        left = e.clientX + this.#offsetX;
                        height = e.clientY - top + this.#offsetY;
                        width = rect.right - e.clientX - this.#offsetX;
                        break;

                    case "BottomRight":
                        height = e.clientY - top + this.#offsetY;
                        width = e.clientX - left + this.#offsetX;
                        break;

                    case "Left":
                        left = e.clientX + this.#offsetX;
                        width = rect.right - e.clientX - this.#offsetX;
                        break;
                }

                this.position(top, left, height, width);
                this.#animationFrameId = null;
            });
        };

        const resizePointerUp = (e: PointerEvent) => {
            const handle = must(e.target, HTMLElement);
            this.dataset.isResizing = false;
            handle.releasePointerCapture(e.pointerId);
            handle.removeEventListener("pointermove", resizePointerMove);
            handle.removeEventListener("pointerup", resizePointerUp);
            handle.removeEventListener("pointercancel", resizePointerUp);
            handle.removeEventListener("lostpointercapture", resizePointerUp);

            if (this.#animationFrameId !== null) {
                cancelAnimationFrame(this.#animationFrameId);
                this.#animationFrameId = null;
            }
        };

        const resizePointerDown = (e: PointerEvent) => {
            if (e.button !== 0 && e.pointerType !== "touch") return;

            const handle = must(e.target, HTMLElement);
            e.preventDefault();
            const rect = this.getRect();
            this.#rect = rect;

            const types = ["Top", "TopLeft", "TopRight", "Right", "Bottom", "BottomLeft", "BottomRight", "Left"];

            for (const type of types) {
                if (handle.classList.contains(type)) {
                    this.#resizeType = type;
                    break;
                }
            }

            switch (this.#resizeType) {
                case "Top":
                    this.#offsetY = rect.top - e.clientY;
                    break;

                case "TopLeft":
                    this.#offsetY = rect.top - e.clientY;
                    this.#offsetX = rect.left - e.clientX;
                    break;

                case "TopRight":
                    this.#offsetY = rect.top - e.clientY;
                    this.#offsetX = rect.right - e.clientX;
                    break;

                case "Right":
                    this.#offsetX = rect.right - e.clientX;
                    break;

                case "Bottom":
                    this.#offsetY = rect.bottom - e.clientY;
                    break;

                case "BottomLeft":
                    this.#offsetY = rect.bottom - e.clientY;
                    this.#offsetX = rect.left - e.clientX;
                    break;

                case "BottomRight":
                    this.#offsetY = rect.bottom - e.clientY;
                    this.#offsetX = rect.right - e.clientX;
                    break;

                case "Left":
                    this.#offsetX = rect.left - e.clientX;
                    break;
            }

            this.dataset.isResizing = true;
            handle.setPointerCapture(e.pointerId);

            handle.addEventListener("pointermove", resizePointerMove,
                {
                    passive: true,
                    signal: this.#eventController.signal
                });

            handle.addEventListener("pointerup", resizePointerUp,
                {
                    passive: true, once: true,
                    signal: this.#eventController.signal
                });

            handle.addEventListener("pointercancel", resizePointerUp,
                {
                    passive: true, once: true,
                    signal: this.#eventController.signal
                });

            handle.addEventListener("lostpointercapture", resizePointerUp,
                {
                    passive: true, once: true,
                    signal: this.#eventController.signal
                });
        };

        const addResizeHandles = (...classes: string[]) => {
            for (const c of classes) {
                const handle = document.createElement("div");
                handle.classList.add("Window-ResizeHandle", c);

                handle.addEventListener("pointerdown", resizePointerDown,
                    {
                        passive: false,
                        signal: this.#eventController.signal
                    });

                this.#element.appendChild(handle);
            }
        };

        addResizeHandles("Top", "Right", "Bottom", "Left",
            "TopLeft", "TopRight", "BottomLeft", "BottomRight");

        // Add bouble-click event on top/bottom resize handle that resize window from top to bottom.
        const handleTop = mustQuerySelector(this.#element, ".Window-ResizeHandle.Top");
        const handleBottom = mustQuerySelector(this.#element, ".Window-ResizeHandle.Bottom");

        const resizeTopToBottom = (e: MouseEvent) => {
            let height = window.innerHeight;

            const taskbar = this.formManager.windowManager.taskbar;

            if (taskbar !== undefined) {
                height = height - taskbar.getRect().height;
            }

            const rect = this.getRect();
            this.position(0, rect.left, height, rect.width);
        };

        handleTop.addEventListener("dblclick", resizeTopToBottom, {
            signal: this.#eventController.signal
        });

        handleBottom.addEventListener("dblclick", resizeTopToBottom, {
            signal: this.#eventController.signal
        });
    };
}

function getFirstWindow(w: Window) {
    const parentWindow = w.modalParentWindow;

    if (parentWindow === undefined) {
        return w;
    }

    return getFirstWindow(parentWindow);
}

function getLastWindow(w: Window) {
    const childWindow = w.modalChildWindow;

    if (childWindow === undefined) {
        return w;
    }

    return getLastWindow(childWindow);
}

class WindowDataset {
    #element;

    constructor(element: HTMLElement) {
        this.#element = element;
    }

    get state(): string {
        const state = this.#element.dataset.state;

        if (state === undefined) {
            return windowStateNormal;
        }

        return state;
    }

    set state(value: string | undefined) {
        if (value === undefined) {
            delete this.#element.dataset.state;
        } else {
            this.#element.dataset.state = value;
        }
    }

    get isModalParent(): boolean {
        return this.#element.dataset.isModalParent !== undefined;
    }

    set isModalParent(value: boolean) {
        if (value === true) {
            this.#element.dataset.isModalParent = "";
        } else {
            delete this.#element.dataset.isModalParent;
        }
    }

    get isModal(): boolean {
        return this.#element.dataset.isModal !== undefined;
    }

    set isModal(value: boolean) {
        if (value === true) {
            this.#element.dataset.isModal = "";
        } else {
            delete this.#element.dataset.isModal;
        }
    }

    get isActive(): boolean {
        return this.#element.dataset.isActive !== undefined;
    }

    set isActive(value: boolean) {
        if (value === true) {
            this.#element.dataset.isActive = "";
        } else {
            delete this.#element.dataset.isActive;
        }
    }

    get isMinimizing(): boolean {
        return this.#element.dataset.isMinimizing !== undefined;
    }

    set isMinimizing(value: boolean) {
        if (value === true) {
            this.#element.dataset.isMinimizing = "";
        } else {
            delete this.#element.dataset.isMinimizing;
        }
    }

    get isMinimized(): boolean {
        return this.#element.dataset.isMinimized !== undefined;
    }

    set isMinimized(value: boolean) {
        if (value === true) {
            this.#element.dataset.isMinimized = "";
        } else {
            delete this.#element.dataset.isMinimized;
        }
    }

    get isUnminimizing(): boolean {
        return this.#element.dataset.isUnminimizing !== undefined;
    }

    set isUnminimizing(value: boolean) {
        if (value === true) {
            this.#element.dataset.isUnminimizing = "";
        } else {
            delete this.#element.dataset.isUnminimizing;
        }
    }

    get isMoving(): boolean {
        return this.#element.dataset.isMoving !== undefined;
    }

    set isMoving(value: boolean) {
        if (value === true) {
            this.#element.dataset.isMoving = "";
        } else {
            delete this.#element.dataset.isMoving;
        }
    }

    get isResizing(): boolean {
        return this.#element.dataset.isResizing !== undefined;
    }

    set isResizing(value: boolean) {
        if (value === true) {
            this.#element.dataset.isResizing = "";
        } else {
            delete this.#element.dataset.isResizing;
        }
    }

    get isFlashing(): boolean {
        return this.#element.dataset.isFlashing !== undefined;
    }

    set isFlashing(value: boolean) {
        if (value === true) {
            this.#element.dataset.isFlashing = "";
        } else {
            delete this.#element.dataset.isFlashing;
        }
    }
}